<template>
  <main class="bg-about-background bg-top pb-20 bg-[length:130rem] h-full bg-no-repeat bg-white pt-28">
    <section class="mx-auto max-w-4xl">
      <div class="mb-6">
        <h2 class="text-gray-900 text-4xl font-medium flex items-center gap-2">
          <UsersIcon class="h-7 w-7 text-primary" />
          Member Directory
        </h2>
        <p class="text-gray-600 mt-2">
          Find members in the network and reach out directly.
        </p>
      </div>
      <div class="mb-6">
        <label
          class="block text-sm font-medium text-gray-700 mb-2"
          for="member-search"
        >
          Search members
        </label>
        <div class="relative">
          <MagnifyingGlassIcon class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            id="member-search"
            v-model="searchQuery"
            class="block w-full rounded-full border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 focus:border-primary focus:ring-primary"
            placeholder="Search by name or email"
            type="text"
          >
        </div>
      </div>
      <div
        v-if="loadingUsers"
        class="text-sm text-gray-500"
      >
        Loading members...
      </div>
      <div
        v-else-if="filteredUsers.length === 0"
        class="text-sm text-gray-500"
      >
        No members found.
      </div>
      <div
        v-else
        class="grid gap-4"
      >
        <div
          v-for="user in filteredUsers"
          :key="user._id"
          class="bg-white border border-gray-200 shadow-sm rounded-2xl p-5"
        >
          <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div class="flex items-start gap-4">
              <div class="flex h-12 w-12 items-center justify-center rounded-full bg-[#f6f1ec] text-sm font-semibold text-gray-700">
                {{ getInitials(user.fullName || user.email) }}
              </div>
              <div>
                <div class="text-gray-900 font-semibold">
                  {{ user.fullName || 'Member' }}
                </div>
                <div
                  v-if="user.company"
                  class="text-sm text-gray-600"
                >
                  {{ user.company }}
                </div>
                <div class="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <div class="flex items-center gap-2">
                    <EnvelopeIcon class="h-4 w-4 text-gray-400" />
                    {{ user.email }}
                  </div>
                  <div
                    v-if="user.phoneNumber"
                    class="flex items-center gap-2"
                  >
                    <PhoneIcon class="h-4 w-4 text-gray-400" />
                    {{ user.phoneNumber }}
                  </div>
                </div>
                <div
                  v-if="formatAddress(user)"
                  class="text-sm text-gray-600"
                >
                  {{ formatAddress(user) }}
                </div>
                <div
                  v-if="user.linkedinUrl || user.xUrl"
                  class="mt-2 flex items-center gap-2"
                >
                  <a
                    v-if="user.linkedinUrl"
                    class="inline-flex items-center rounded-full bg-gray-100 p-2 text-gray-700 hover:bg-gray-200"
                    :href="user.linkedinUrl"
                    rel="noreferrer"
                    target="_blank"
                  >
                    <svg
                      aria-hidden="true"
                      class="h-4 w-4"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M19.99 3H4.01C3.45 3 3 3.45 3 4.01v15.98C3 20.55 3.45 21 4.01 21h15.98c.56 0 1.01-.45 1.01-1.01V4.01C21 3.45 20.55 3 19.99 3zM8.34 18.34H5.67V9.75h2.67v8.59zM7 8.58a1.55 1.55 0 1 1 0-3.1 1.55 1.55 0 0 1 0 3.1zm11.34 9.76h-2.66v-4.18c0-.99-.02-2.27-1.38-2.27-1.38 0-1.59 1.08-1.59 2.2v4.25H10.05V9.75h2.55v1.17h.04c.35-.66 1.2-1.36 2.47-1.36 2.64 0 3.13 1.74 3.13 4.01v4.77z"
                      />
                    </svg>
                  </a>
                  <a
                    v-if="user.xUrl"
                    class="inline-flex items-center rounded-full bg-gray-100 p-2 text-gray-700 hover:bg-gray-200"
                    :href="user.xUrl"
                    rel="noreferrer"
                    target="_blank"
                  >
                    <svg
                      aria-hidden="true"
                      class="h-4 w-4"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M18.9 2h3.3l-7.2 8.2L23.4 22h-6.8l-5.3-7-6.1 7H1.9l7.7-8.8L.6 2h7l4.8 6.3L18.9 2zm-1.2 18h1.8L7.3 4H5.3l12.4 16z"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-3 md:flex-col md:items-end">
              <button
                class="text-sm font-medium text-gray-900 border border-gray-300 rounded-full px-4 py-2 hover:bg-gray-100 inline-flex items-center gap-1"
                type="button"
                @click="toggleContact(user._id)"
              >
                <UserPlusIcon
                  v-if="!isContact(user._id)"
                  class="h-4 w-4"
                />
                <CheckIcon
                  v-else
                  class="h-4 w-4"
                />
                {{ isContact(user._id) ? 'Added' : 'Add to Contacts' }}
              </button>
              <div class="flex items-center gap-2">
                <a
                  class="text-sm font-medium text-gray-900 inline-flex items-center gap-1 rounded-full bg-primary/20 px-3 py-1.5 hover:bg-primary/30"
                  :href="`mailto:${user.email}`"
                >
                  <EnvelopeIcon class="h-4 w-4 text-gray-900" />
                  Email
                </a>
                <a
                  v-if="user.phoneNumber"
                  class="text-sm font-medium text-gray-900 inline-flex items-center gap-1 rounded-full bg-primary/20 px-3 py-1.5 hover:bg-primary/30"
                  :href="`tel:${user.phoneNumber}`"
                >
                  <PhoneIcon class="h-4 w-4 text-gray-900" />
                  Call
                </a>
              </div>
            </div>
          </div>
          <div class="mt-3 text-xs text-gray-500">
            Joined {{ formatDate(user.createdAt) }}
          </div>
        </div>
      </div>
    </section>
    <section class="mx-auto max-w-4xl mt-12">
      <div class="mb-6">
        <h2 class="text-gray-900 text-3xl font-medium flex items-center gap-2">
          <UserGroupIcon class="h-6 w-6 text-primary" />
          My Contacts
        </h2>
        <p class="text-gray-600 mt-2">
          Members you have saved for quick access.
        </p>
      </div>
      <div
        v-if="contactUsers.length === 0"
        class="text-sm text-gray-500"
      >
        You have not added any contacts yet.
      </div>
      <div
        v-else
        class="grid gap-4"
      >
        <div
          v-for="user in contactUsers"
          :key="user._id"
          class="bg-white border border-gray-200 shadow-sm rounded-2xl p-5"
        >
          <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div class="flex items-start gap-4">
              <div class="flex h-12 w-12 items-center justify-center rounded-full bg-[#f6f1ec] text-sm font-semibold text-gray-700">
                {{ getInitials(user.fullName || user.email) }}
              </div>
              <div>
                <div class="text-gray-900 font-semibold">
                  {{ user.fullName || 'Member' }}
                </div>
                <div
                  v-if="user.company"
                  class="text-sm text-gray-600"
                >
                  {{ user.company }}
                </div>
                <div class="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <div class="flex items-center gap-2">
                    <EnvelopeIcon class="h-4 w-4 text-gray-400" />
                    {{ user.email }}
                  </div>
                  <div
                    v-if="user.phoneNumber"
                    class="flex items-center gap-2"
                  >
                    <PhoneIcon class="h-4 w-4 text-gray-400" />
                    {{ user.phoneNumber }}
                  </div>
                </div>
                <div
                  v-if="formatAddress(user)"
                  class="text-sm text-gray-600"
                >
                  {{ formatAddress(user) }}
                </div>
                <div
                  v-if="user.linkedinUrl || user.xUrl"
                  class="mt-2 flex items-center gap-2"
                >
                  <a
                    v-if="user.linkedinUrl"
                    class="inline-flex items-center rounded-full bg-gray-100 p-2 text-gray-700 hover:bg-gray-200"
                    :href="user.linkedinUrl"
                    rel="noreferrer"
                    target="_blank"
                  >
                    <svg
                      aria-hidden="true"
                      class="h-4 w-4"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M19.99 3H4.01C3.45 3 3 3.45 3 4.01v15.98C3 20.55 3.45 21 4.01 21h15.98c.56 0 1.01-.45 1.01-1.01V4.01C21 3.45 20.55 3 19.99 3zM8.34 18.34H5.67V9.75h2.67v8.59zM7 8.58a1.55 1.55 0 1 1 0-3.1 1.55 1.55 0 0 1 0 3.1zm11.34 9.76h-2.66v-4.18c0-.99-.02-2.27-1.38-2.27-1.38 0-1.59 1.08-1.59 2.2v4.25H10.05V9.75h2.55v1.17h.04c.35-.66 1.2-1.36 2.47-1.36 2.64 0 3.13 1.74 3.13 4.01v4.77z"
                      />
                    </svg>
                  </a>
                  <a
                    v-if="user.xUrl"
                    class="inline-flex items-center rounded-full bg-gray-100 p-2 text-gray-700 hover:bg-gray-200"
                    :href="user.xUrl"
                    rel="noreferrer"
                    target="_blank"
                  >
                    <svg
                      aria-hidden="true"
                      class="h-4 w-4"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M18.9 2h3.3l-7.2 8.2L23.4 22h-6.8l-5.3-7-6.1 7H1.9l7.7-8.8L.6 2h7l4.8 6.3L18.9 2zm-1.2 18h1.8L7.3 4H5.3l12.4 16z"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-3 md:flex-col md:items-end">
              <button
                class="text-sm font-medium text-gray-900 border border-gray-300 rounded-full px-4 py-2 hover:bg-gray-100 inline-flex items-center gap-1"
                type="button"
                @click="toggleContact(user._id)"
              >
                <CheckIcon class="h-4 w-4" />
                Remove
              </button>
              <div class="flex items-center gap-2">
                <a
                  class="text-sm font-medium text-gray-900 inline-flex items-center gap-1 rounded-full bg-primary/20 px-3 py-1.5 hover:bg-primary/30"
                  :href="`mailto:${user.email}`"
                >
                  <EnvelopeIcon class="h-4 w-4 text-gray-900" />
                  Email
                </a>
                <a
                  v-if="user.phoneNumber"
                  class="text-sm font-medium text-gray-900 inline-flex items-center gap-1 rounded-full bg-primary/20 px-3 py-1.5 hover:bg-primary/30"
                  :href="`tel:${user.phoneNumber}`"
                >
                  <PhoneIcon class="h-4 w-4 text-gray-900" />
                  Call
                </a>
              </div>
            </div>
          </div>
          <div class="mt-3 text-xs text-gray-500">
            Joined {{ formatDate(user.createdAt) }}
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, ref, watchEffect} from 'vue';
import axios from 'axios';
import {User} from '@/types';
import {settingsKey} from "@/composables/useSettings";
import {useHead} from "@vueuse/head";
import {inject} from "vue";
import {
  CheckIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  UserGroupIcon,
  UserPlusIcon,
  UsersIcon,
} from "@heroicons/vue/24/outline";

export default defineComponent({
  name: 'Directory',
  components: {
    CheckIcon,
    EnvelopeIcon,
    MagnifyingGlassIcon,
    PhoneIcon,
    UserGroupIcon,
    UserPlusIcon,
    UsersIcon,
  },
  setup() {
    const {settings} = inject(settingsKey)!;
    const users = ref<User[]>([]);
    const filteredUsers = ref<User[]>([]);
    const loadingUsers = ref(false);
    const searchQuery = ref('');
    const contactIds = ref<string[]>([]);

    const fetchUsers = async (): Promise<void> => {
      loadingUsers.value = true;
      try {
        const response = await axios.get<User[]>('/user/all');
        users.value = response.data;
        filteredUsers.value = response.data;
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        loadingUsers.value = false;
      }
    };

    const filterUsers = (): void => {
      const query = searchQuery.value.trim().toLowerCase();
      filteredUsers.value = query
        ? users.value.filter((user) =>
            (user.fullName || '').toLowerCase().includes(query) ||
            user.email.toLowerCase().includes(query) ||
            (user.company || '').toLowerCase().includes(query)
        )
        : users.value;
    };

    const getInitials = (value: string): string => {
      const parts = value.trim().split(' ');
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    };

    const formatDate = (value?: string): string => {
      if (!value) return 'Unknown date';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Unknown date';
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(date);
    };

    const formatAddress = (user: User): string => {
      const parts = [
        user.address?.city,
        user.address?.state,
        user.address?.country,
      ].filter(Boolean);
      return parts.join(', ');
    };

    const loadContacts = (): void => {
      if (typeof window === 'undefined') return;
      try {
        const stored = localStorage.getItem('nevnet-contacts');
        contactIds.value = stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error('Failed to load contacts:', error);
        contactIds.value = [];
      }
    };

    const saveContacts = (): void => {
      if (typeof window === 'undefined') return;
      localStorage.setItem('nevnet-contacts', JSON.stringify(contactIds.value));
    };

    const toggleContact = (userId: string): void => {
      if (contactIds.value.includes(userId)) {
        contactIds.value = contactIds.value.filter((id) => id !== userId);
      } else {
        contactIds.value = [...contactIds.value, userId];
      }
      saveContacts();
    };

    const contactUsers = computed(() =>
      users.value.filter((user) => contactIds.value.includes(user._id))
    );

    watchEffect(() => {
      filterUsers();
    });

    onMounted(async () => {
      loadContacts();
      await fetchUsers();
    });

    watchEffect(() => {
      if (settings.value) {
        useHead({
          title: 'Directory',
          meta: [
            {name: 'description', content: '...'},
            {name: 'keywords', content: '...'},
            {name: 'author', content: '...'},
          ],
        });
      }
    });

    return {
      filteredUsers,
      loadingUsers,
      contactUsers,
      searchQuery,
      getInitials,
      formatDate,
      formatAddress,
      toggleContact,
      isContact: (userId: string) => contactIds.value.includes(userId),
    };
  },
});
</script>
